import { apiClient } from '../api/client/client';
import type { ServiceResultT } from '../api/types/common/ServiceResultT';
import { ANALYTICS_INGEST_URL } from './config';
import { createEngagementTracker } from './engagement';
import {
  getOrCreateVisitorId,
  loadSessionState,
  nextSequenceNumber,
  resetAnalyticsSession,
  saveSessionState,
  touchSession,
} from './identity';
import { isAnalyticsExcludedPath, normalizeRoute } from './routeNormalization';
import { getAnalyticsConsent, isAnalyticsConsentGranted } from '../utils/cookieConsent';

export type AnalyticsPageViewPayload = {
  eventId: string;
  sequenceNumber: number;
  pageType: string;
  pageLabel: string;
  normalizedPath: string;
  entityId?: string;
  viewedAt: string;
  engagedMs: number;
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

type CustomEventPayload = {
  eventId: string;
  eventName: string;
  occurredAt: string;
  metadata?: Record<string, string>;
};

export type AnalyticsBatchPayload = {
  sessionId: string;
  visitorId?: string | null;
  userId?: string | null;
  sessionStartedAt: string;
  lastActivityAt: string;
  hadAnalyticsConsent: boolean;
  landingPageType?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  pageViews: AnalyticsPageViewPayload[];
  customEvents: CustomEventPayload[];
};

let currentUserId: string | null = null;
let engagement = createEngagementTracker();
let lastTrackedKey: string | null = null;
let activePage: AnalyticsPageViewPayload | null = null;

const newEventId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const apiBase = (): string =>
  (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');

const parseUtm = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
  };
};

const referrerHost = (): string | undefined => {
  try {
    if (!document.referrer) return undefined;
    return new URL(document.referrer).host;
  } catch {
    return undefined;
  }
};

const buildBatch = (
  pageViews: AnalyticsPageViewPayload[],
  customEvents: CustomEventPayload[] = [],
): AnalyticsBatchPayload | null => {
  const consent = getAnalyticsConsent();
  if (consent === 'rejected') return null;

  const session = loadSessionState() ?? touchSession();
  return {
    sessionId: session.sessionId,
    visitorId: getOrCreateVisitorId(),
    userId: currentUserId,
    sessionStartedAt: session.startedAt,
    lastActivityAt: new Date().toISOString(),
    hadAnalyticsConsent: isAnalyticsConsentGranted(),
    landingPageType: session.landingPageType,
    pageViews,
    customEvents,
  };
};

export function setAnalyticsUserId(userId: string | null): void {
  currentUserId = userId;
}

export function onAnalyticsLogout(): void {
  void finalizeActivePage(true);
  currentUserId = null;
  resetAnalyticsSession();
  engagement.stop();
  lastTrackedKey = null;
  activePage = null;
}

export async function flushAnalyticsBatch(
  pageViews: AnalyticsPageViewPayload[] = [],
  customEvents: CustomEventPayload[] = [],
  useBeacon = false,
): Promise<void> {
  const batch = buildBatch(pageViews, customEvents);
  if (!batch || (batch.pageViews.length === 0 && batch.customEvents.length === 0)) return;

  const body = JSON.stringify(batch);
  const url = `${apiBase()}${ANALYTICS_INGEST_URL}`;

  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    await apiClient.post<ServiceResultT<unknown>>(ANALYTICS_INGEST_URL, batch);
  } catch {
    // non-blocking
  }
}

async function finalizeActivePage(useBeacon = false): Promise<void> {
  if (!activePage) return;
  activePage.engagedMs += engagement.flushEngagedMs();
  await flushAnalyticsBatch([activePage], [], useBeacon);
  activePage = null;
}

export function trackPageView(pathname: string, search: string): void {
  const consent = getAnalyticsConsent();
  if (consent === 'rejected') return;
  if (isAnalyticsExcludedPath(pathname)) return;

  const routeKey = `${pathname}|${search}`;
  if (lastTrackedKey === routeKey) return;
  lastTrackedKey = routeKey;

  void finalizeActivePage();

  const normalized = normalizeRoute(pathname);
  const session = touchSession(normalized.pageType);
  saveSessionState({
    ...session,
    lastActivityAt: new Date().toISOString(),
    landingPageType: session.landingPageType ?? normalized.pageType,
  });

  engagement.stop();
  engagement = createEngagementTracker();
  engagement.start();

  const utm = parseUtm(search);
  activePage = {
    eventId: newEventId(),
    sequenceNumber: nextSequenceNumber(),
    pageType: normalized.pageType,
    pageLabel: normalized.pageLabel,
    normalizedPath: normalized.normalizedPath,
    entityId: normalized.entityId,
    viewedAt: new Date().toISOString(),
    engagedMs: 0,
    referrerHost: referrerHost(),
    ...utm,
  };

  void flushAnalyticsBatch([{ ...activePage }]);
}

export function trackCustomEvent(
  eventName: string,
  metadata?: Record<string, string>,
): void {
  void flushAnalyticsBatch([], [
    {
      eventId: newEventId(),
      eventName,
      occurredAt: new Date().toISOString(),
      metadata,
    },
  ]);
}

export function initAnalyticsPageExitFlush(): () => void {
  const handler = () => {
    void finalizeActivePage(true);
  };
  window.addEventListener('pagehide', handler);
  return () => window.removeEventListener('pagehide', handler);
}

/** @internal test helper */
export function resetAnalyticsTrackerState(): void {
  activePage = null;
  lastTrackedKey = null;
  engagement.stop();
  engagement = createEngagementTracker();
}
